import React, { useState } from "react";
import axios from "axios";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { QuoteSuccess } from "../QuoteSuccess";
import { company } from "../../data/mock";
import { API } from "../../lib/storeApi";
import { appendLeadSource } from "../../lib/leadSource";
import * as D from "../../data/audioQuote";
import { ScopeStep, QuickStep, LayoutStep, SpeakersStep, SourcingStep, SurveyStep, OutdoorStep } from "./AudioSteps";
import { FinalStep } from "./AudioFinalStep";

const INITIAL = {
  scope: "", quick: {}, layout: "", zones: 1, eco: "", speakers: [], subs: 0, ceiling: "",
  sourcing: "", ownAmp: "", ownSpeakers: "", control: "", rooms: [], roomsOther: "", roomSize: "", wall: "", display: "",
  access: [], wires: "", wireGauge: "", wireEnd: "", power: "", network: "", location: "",
  outdoor: "", outQty: 1, outLoc: [], outStyle: "", sources: [], quoteOpt: "", date: "", slot: "",
  first: "", last: "", email: "", phone: "", address: "", notes: "",
};

const STEPS = {
  scope: { title: "Audio quote", C: ScopeStep, ok: (a) => !!a.scope },
  quick: { title: "Quick audio jobs", C: QuickStep, ok: (a) => Object.keys(a.quick).length > 0 && Object.values(a.quick).every(Boolean) },
  layout: { title: "System layout and zones", C: LayoutStep, ok: (a) => (a.scope === "Outdoor area only" || !!a.layout) && !!a.eco },
  speakers: { title: "Speakers and subwoofers", C: SpeakersStep, ok: (a) => a.speakers.length > 0 && !!a.ceiling },
  sourcing: { title: "Equipment and control", C: SourcingStep, ok: (a) => !!a.sourcing && !!a.control },
  survey: { title: "Room, structure, and cabling", C: SurveyStep, ok: (a) => !!a.wall && !!a.display && !!a.wires && !!a.power && !!a.network && !!a.location },
  outdoor: { title: "Outdoor audio", C: OutdoorStep, ok: (a) => a.outdoor === "No" || (a.outdoor === "Yes" && a.outLoc.length > 0 && !!a.outStyle) },
  final: { title: "Sources, photos, and booking", C: null, ok: (a) => !!a.quoteOpt && a.first.trim() && a.last.trim() && a.email.trim() && a.phone.trim() },
};

const flow = (a) => {
  if (a.scope === D.QUICK_SCOPE) return ["scope", "quick", "final"];
  const s = ["scope", "layout", "speakers", "sourcing", "survey"];
  if (a.scope && a.scope !== "TV / Media Room setup only") s.push("outdoor");
  return [...s, "final"];
};

const list = (v) => (v.length ? v.join(", ") : "None");
const summarize = (a, bookOnline, nFiles) => {
  const L = [`Audio project scope: ${a.scope}`];
  if (a.scope === D.QUICK_SCOPE) {
    Object.entries(a.quick).forEach(([j, s]) => L.push(`Quick job: ${j}. Equipment: ${s}`));
  } else {
    if (a.scope !== "Outdoor area only") L.push(`Surround layout: ${a.layout}`);
    L.push(`Zones or rooms: ${a.zones}`, `Streaming ecosystem: ${a.eco}`, `Speaker types: ${list(a.speakers)}`, `Subwoofers: ${a.subs}`, `Ceiling: ${a.ceiling}`);
    L.push(`Equipment sourcing: ${a.sourcing}`);
    if (a.sourcing === D.OWN) L.push(`Current receiver or amp: ${a.ownAmp || "-"}`, `Current speakers: ${a.ownSpeakers || "-"}`);
    L.push(`Control preference: ${a.control}`, `Target rooms: ${list(a.rooms)}${a.roomsOther ? ` (${a.roomsOther})` : ""}`, `Room size: ${a.roomSize || "-"}`);
    L.push(`Wall type: ${a.wall}`, `TV and display: ${a.display}`, `Wire access: ${list(a.access)}`, `Pre-existing speaker wires: ${a.wires}`);
    if (a.wires === "Yes") L.push(`Wire type or gauge: ${a.wireGauge || "-"}`, `Wires end at: ${a.wireEnd || "-"}`);
    L.push(`Power outlet near equipment: ${a.power}`, `Network: ${a.network}`, `Equipment location: ${a.location}`);
    if (a.outdoor && a.scope !== "TV / Media Room setup only") L.push(`Outdoor speakers: ${a.outdoor}`);
    if (a.outdoor === "Yes" && a.scope !== "TV / Media Room setup only") L.push(`Outdoor speaker count: ${a.outQty}`, `Outdoor locations: ${list(a.outLoc)}`, `Outdoor style: ${a.outStyle}`);
    L.push(`Media sources: ${list(a.sources)}`);
  }
  L.push(`Quote option: ${a.quoteOpt}`);
  if (a.quoteOpt === D.QUOTE_A) L.push(`Files attached: ${nFiles}`);
  if (a.quoteOpt === D.QUOTE_B) L.push(`Preferred survey: ${a.date || "flexible"}${a.slot ? ` at ${a.slot}` : ""}`);
  L.push(`Service address: ${a.address || "-"}`);
  if (a.notes) L.push(`Notes: ${a.notes}`);
  L.push(bookOnline ? "Customer chose: Book my install" : "Customer chose: Just send me the quote");
  return `[audio wizard]\n${L.join("\n")}`;
};

const Done = ({ name }) => (
  <div data-testid="aq-done" ref={(el) => el && setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 50)} className="scroll-mt-28">
    <QuoteSuccess name={name} smsHref={company.audioPhotoSms}>
      We've got your audio details. Chris will follow up with your free itemized quote. {D.PRICE_LINE}
    </QuoteSuccess>
  </div>
);

export const AudioQuote = () => {
  const [a, setA] = useState(INITIAL);
  const [files, setFiles] = useState([]);
  const [i, setI] = useState(0);
  const [state, setState] = useState({ sending: false, done: false, error: "" });
  const set = (patch) => setA((cur) => ({ ...cur, ...patch }));
  const steps = flow(a);
  const key = steps[Math.min(i, steps.length - 1)];
  const step = STEPS[key];
  const isLast = key === "final";
  const filesOk = a.quoteOpt !== D.QUOTE_A || files.length > 0;
  const canNext = step.ok(a) && (!isLast || filesOk);
  const go = (n) => { setI(n); window.scrollTo({ top: 0, behavior: "smooth" }); };

  const submit = async (bookOnline) => {
    if (!canNext) return;
    setState({ sending: true, done: false, error: "" });
    const fd = new FormData();
    fd.append("name", `${a.first.trim()} ${a.last.trim()}`);
    fd.append("phone", a.phone.trim());
    fd.append("email", a.email.trim());
    fd.append("kind", "audio");
    fd.append("message", summarize(a, bookOnline, files.length));
    if (a.quoteOpt === D.QUOTE_A) files.forEach((f) => fd.append("photos", f));
    try {
      await axios.post(`${API}/photo-quote`, appendLeadSource(fd));
      setState({ sending: false, done: true, error: "" });
    } catch (err) {
      setState({ sending: false, done: false, error: (typeof err.response?.data?.detail === "string" && err.response.data.detail) || "Something went wrong. Please try again or text (252) 545-0011." });
    }
  };

  if (state.done) return <Done name={a.first.trim()} />;
  const C = step.C;
  return (
    <div data-testid="audio-quote">
      <div className="flex items-center gap-3 mb-6">
        <div className="flex-1 h-2 rounded-full bg-slate-200 overflow-hidden">
          <div className="h-full bg-[#3b9dff] transition-all" style={{ width: `${((i + 1) / steps.length) * 100}%` }} />
        </div>
        <span className="text-sm font-semibold text-slate-500 whitespace-nowrap" data-testid="aq-step-indicator">Step {i + 1}/{steps.length}</span>
      </div>
      <h1 className="text-3xl sm:text-4xl font-extrabold text-[#0a1f44]">{step.title}</h1>
      <p className="text-slate-500 mt-1" data-testid="aq-price-line">{D.PRICE_LINE}</p>
      <div className="mt-7 max-w-3xl">
        {isLast ? <FinalStep a={a} set={set} files={files} setFiles={setFiles} /> : <C a={a} set={set} />}
      </div>
      {state.error && <p className="mt-6 text-sm text-red-600" data-testid="aq-error">{state.error}</p>}
      <div className="flex flex-wrap items-center justify-between gap-3 mt-10 max-w-3xl">
        <button onClick={() => go(i - 1)} disabled={i === 0} data-testid="aq-back-btn" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-slate-500 disabled:opacity-40 hover:text-[#0a1f44]"><ArrowLeft className="w-4 h-4" /> Back</button>
        {!isLast ? (
          <button onClick={() => canNext && go(i + 1)} disabled={!canNext} data-testid="aq-next-btn" className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-[#3b9dff] hover:bg-[#2b8ae8] text-white font-bold disabled:opacity-40">Next <ArrowRight className="w-4 h-4" /></button>
        ) : (
          <div className="flex flex-wrap gap-3">
            <button onClick={() => submit(false)} disabled={!canNext || state.sending} data-testid="aq-request-quote-btn" className="px-6 py-3 rounded-full border border-slate-200 text-[#0a1f44] font-semibold disabled:opacity-40">Just send me the quote</button>
            <button onClick={() => submit(true)} disabled={!canNext || state.sending} data-testid="aq-book-btn" className="px-7 py-3 rounded-full bg-[#3b9dff] hover:bg-[#2b8ae8] text-white font-bold disabled:opacity-40">{state.sending ? "Sending..." : "Book my install"}</button>
          </div>
        )}
      </div>
      {isLast && <p className="mt-4 text-xs text-slate-400 max-w-3xl" data-testid="aq-footer-price">{D.PRICE_LINE}</p>}
    </div>
  );
};
